import React, { useState, useEffect, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { 
  getDatabase, 
  ref, 
  onValue, 
  get,
  serverTimestamp,
  push,
  set,
  runTransaction
} from 'firebase/database';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import MorningHabits from './components/MorningHabits';
import MorningChatbot from './components/MorningChatbot';
import './App.css';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyA_qM-D4UQKhCFPKwJvrKQ9u7IS8Vp02Q4",
  authDomain: "fbdeployai.firebaseapp.com",
  databaseURL: "https://fbdeployai-default-rtdb.asia-southeast1.firebasedatabase.app",
  projectId: "fbdeployai",
  storageBucket: "fbdeployai.firebasestorage.app",
  messagingSenderId: "209425479484",
  appId: "1:209425479484:web:172bbf03828341e47a5039"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
auth.useDeviceLanguage();
const database = getDatabase(app);
const provider = new GoogleAuthProvider();
provider.setCustomParameters({
  prompt: 'select_account'
});

// Helper: Convert UTC to IST (UTC+5:30)
const getISTTime = () => {
  const now = new Date();
  const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
  const istTime = new Date(utcTime + (5.5 * 60 * 60 * 1000));
  return istTime;
};

// Calculate reward based on check-in time (IST)
const calculateReward = (checkInTime) => {
  const hour = checkInTime.getHours();
  const minutes = checkInTime.getMinutes();
  const timeInMinutes = hour * 60 + minutes;

  if (timeInMinutes >= 4 * 60 && timeInMinutes < 5 * 60) return 25; // 4:00 AM - 4:59 AM
  if (timeInMinutes >= 5 * 60 && timeInMinutes < 6 * 60) return 10; // 5:00 AM - 5:59 AM
  if (timeInMinutes >= 6 * 60 && timeInMinutes < 7 * 60) return 5;  // 6:00 AM - 6:59 AM
  if (timeInMinutes >= 7 * 60 && timeInMinutes < 7 * 60 + 20) return 2; // 7:00 AM - 7:20 AM
  return 0;
};

const formatMinutesToLabel = (minutes) => {
  if (minutes == null || Number.isNaN(minutes)) return null;
  const hours = Math.floor(minutes / 60) % 24;
  const mins = Math.round(minutes % 60);
  const date = new Date();
  date.setHours(hours, mins, 0, 0);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const motivationQuotes = [
  'The morning was full of sunlight and hope.',
  'Each sunrise brings a new page in your story.',
  'The secret of getting ahead is getting started.',
  'Your future is created by what you do today, not tomorrow.',
  'Small steps every day add up to big results.',
  'One new habit can change the whole day.',
  'Consistency is the bridge between goals and accomplishments.',
  'Today is a perfect day to build the habits that matter.',
  'Start with what you can do now and keep moving forward.',
  'Great mornings begin with simple, intentional choices.',
  'The way you start your day can set the tone for the rest of it.',
  'Do what you can, with what you have, where you are.',
  'Success is built one small habit at a time.',
  'A positive morning routine creates a more productive day.',
  'A little progress each day adds up to big results.',
  'Discipline is choosing between what you want now and what you want most.',
  'The best way to predict the future is to create it.',
  'Wake up with determination and go to bed with satisfaction.',
  'The first step is always the hardest — but it matters most.',
  'Create a habit of getting up early and success will follow.',
  'Morning routines are the foundation of daily success.',
  'Wake up with purpose and the day becomes your ally.',
  'Begin each day with an act of self-discipline.',
  'Your habits will determine your future.',
  'Make every morning count.',
  'Set the tone for the day with one positive action.',
  'Small habits repeated daily shape your life.',
  'The daily routine of discipline will carry you forward.',
  'Today is the opportunity to build tomorrow’s success.',
  'The best preparation for tomorrow is doing your best today.',
  'Good habits are the key to a strong morning routine.',
  'Every day is a fresh start; take it one habit at a time.',
  'A strong morning begins with one small habit.',
  'Let your habits lift your energy and focus.',
  'Mornings are a chance to renew your commitment to yourself.',
  'A focused morning is a productive day in the making.',
  'Consistency creates momentum, and momentum creates change.',
  'Success is the sum of small efforts repeated day in and day out.',
  'Make the time to be intentional with your morning.',
  'A good morning routine is the strongest part of your day.',
  'Make small choices today that your future self will thank you for.',
  'Your morning habits are the keys to your long-term progress.',
  'The things you do every morning matter more than you think.',
  'If you can control the first hour of the day, you can control the day.',
  'The discipline of the morning will shape your whole day.',
  'A positive start creates a positive chain reaction.',
  'Good habits are the backbone of a healthy routine.',
  'Mornings are the time to build momentum for the rest of the day.',
  'Begin the day with intention and follow it with action.',
  'Habits built in the morning carry through the whole day.',
  'If you want to be productive, protect your mornings.',
  'The morning is a blank page — write something good.',
  'Build habits that support the life you want.',
  'The first 10 minutes of your morning can define the day.',
  'Morning effort is a small price for huge gains.',
  'Start with one thing that helps you feel present and energized.',
  'The right morning habit can change your entire mindset.',
  'Do something every morning that moves you closer to your goals.',
  'Use the morning to set the tone, not react to what happens.',
  'One positive choice first thing can lead to many more.',
  'Build your morning around what makes you feel capable and calm.',
  'A great morning is the result of a few powerful tiny habits.',
  'The best thing about the future is you can create it today.',
  'Let your habits create your tomorrow, starting now.',
  'The first step today is the beginning of something bigger.',
  'A simple routine in the morning can produce enormous results.',
  'Make your mornings intentional and the rest follows.',
  'There is strength in routine and power in small consistent steps.',
  'Good mornings lead to better days and better weeks.',
  'Create a habit system that supports your daily focus.',
  'The beginning of your day can be the best part of your day.',
  'Start with a habit that feeds your energy, focus, and joy.',
  'Small habits are the building blocks of great mornings.',
  'Mornings are an opportunity for steady improvement.',
  'Good habits are the path to steady achievement.',
  'A positive morning routine is an investment in yourself.',
  'The morning is where success habits are formed.',
  'Begin each day by setting and respecting your priorities.',
  'Your morning habits can become your strongest advantage.',
  'Start small, stay consistent, and watch progress grow.',
  'A better day starts with one intentional habit.',
  'Make your first action of the day something that builds momentum.',
  'The best time to start a new habit is the morning.',
  'One good morning habit is worth a thousand good intentions.',
  'Habits are the compound interest of self-improvement.',
  'The morning is the most powerful time to build your best self.',
  'Small habits in the morning lead to extraordinary results.',
  'Use your morning to move toward the life you want.',
  'Wake up and make it a morning worth remembering.',
  'A consistent start leads to a consistent finish.',
  'The morning is where your daily story begins.',
  'Your daily habits are the foundation of your long-term success.',
  'The day belongs to those who show up for their morning routine.',
  'Building a habit is a way to prove to yourself that you can do it.',
  'Today is a chance to be better than yesterday through small habits.',
  'Morning habits are the quiet victories that lead to big changes.',
  'Your morning routine is the compass for your day.',
  'Take control of your morning and the rest of your day will follow.',
  'A consistent morning habit creates stability, energy, and confidence.',
  'The best mornings happen when you start with intention.',
  'Make every morning a fresh start toward your biggest goals.',
  'Start the day with a habit that makes you feel powerful.',
  'Create the morning you want by choosing one positive habit.',
  'Small steps in the morning are the building blocks of big success.',
  'Start your day with good habits and your day will follow in good rhythm.',
  'The way you do mornings matters more than most people realize.',
  'A great morning starts with a habit you can keep doing daily.',
  'Your morning choices are the seeds of tomorrow’s victories.'
];

const getRandomMotivationQuote = () => {
  const index = Math.floor(Math.random() * motivationQuotes.length);
  return motivationQuotes[index];
};

const parseCheckInMinutes = (entry) => {
  if (!entry) return null;
  if (entry.time && typeof entry.time === 'string') {
    const match = entry.time.match(/(\d+):(\d+)/);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      const isPM = /PM/i.test(entry.time);
      const isAM = /AM/i.test(entry.time);
      if (isPM && hours < 12) hours += 12;
      if (isAM && hours === 12) hours = 0;
      return hours * 60 + minutes;
    }
  }
  const ts = entry.ts || entry.timestamp;
  if (ts) {
    const date = new Date(ts);
    const utcTime = date.getTime() + (date.getTimezoneOffset() * 60000);
    const istDate = new Date(utcTime + (5.5 * 60 * 60 * 1000));
    return istDate.getHours() * 60 + istDate.getMinutes();
  }
  return null;
};

const getDateKeyIST = (entry) => {
  const ts = entry.ts || entry.timestamp;
  if (!ts) return null;
  const date = new Date(ts);
  const utcTime = date.getTime() + (date.getTimezoneOffset() * 60000);
  const istDate = new Date(utcTime + (5.5 * 60 * 60 * 1000));
  return istDate.toISOString().slice(0, 10);
};

const computeDailyMetrics = (logEntries) => {
  const entries = Object.values(logEntries || {}).filter((entry) => entry);
  const dailyMinutes = {};

  entries.forEach((entry) => {
    const dayKey = getDateKeyIST(entry);
    const minutes = parseCheckInMinutes(entry);
    if (!dayKey || minutes == null) return;

    if (dailyMinutes[dayKey] == null || minutes < dailyMinutes[dayKey]) {
      dailyMinutes[dayKey] = minutes;
    }
  });

  const validDays = Object.keys(dailyMinutes).sort();
  const dailyValues = validDays.map((day) => dailyMinutes[day]).filter((minutes) => minutes != null);
  const averageMinutes = dailyValues.length > 0 ? dailyValues.reduce((sum, m) => sum + m, 0) / dailyValues.length : null;

  const todayKey = getISTTime().toISOString().slice(0, 10);
  let streak = 0;
  let cursor = new Date(todayKey);
  while (true) {
    const key = cursor.toISOString().slice(0, 10);
    if (!dailyMinutes.hasOwnProperty(key)) break;
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }

  const now = getISTTime();
  const threshold = new Date(now);
  threshold.setDate(threshold.getDate() - 6);
  const last7Days = validDays.filter((dayKey) => new Date(dayKey) >= threshold).length;

  const consistencyScore = averageMinutes == null ? 0 : Math.round(
    Math.min(100,
      Math.max(0,
        35 + streak * 7 + last7Days * 6 - ((averageMinutes - 300) / 2)
      )
    )
  );

  return {
    averageMinutes,
    averageLabel: averageMinutes != null ? formatMinutesToLabel(averageMinutes) : 'No check-ins yet',
    streak,
    consistencyScore,
    daysCheckedIn: validDays.length,
    last7DayCount: last7Days
  };
};

const getMorningNotificationMessage = (now, hasCheckedInToday) => {
  const coins = calculateReward(now);
  if (coins === 0 || hasCheckedInToday) return null;

  const formattedTime = now.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  return `Hey! It is ${formattedTime} IST — check in now to receive ${coins} morning coins.`;
};

function App() {
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState({});
  const [totalCoins, setTotalCoins] = useState(0);
  const [leaderboardUsers, setLeaderboardUsers] = useState([]);
  const [hasCheckedInToday, setHasCheckedInToday] = useState(false);
  const [activeTab, setActiveTab] = useState('checkin'); // 'checkin' | 'deals' | 'feed' | 'article'
  const [activeArticle, setActiveArticle] = useState(null);
  const [toast, setToast] = useState(null);
  const [userRedemptions, setUserRedemptions] = useState({});
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [allUsersData, setAllUsersData] = useState([]);
  const [adminSortField, setAdminSortField] = useState('coins');
  const [adminSortDirection, setAdminSortDirection] = useState('desc');
  const [adminMinCoins, setAdminMinCoins] = useState('');
  const [adminLastLoginAfter, setAdminLastLoginAfter] = useState('');
  const [userHabits, setUserHabits] = useState({});
  const [habitTracking, setHabitTracking] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth > 1200 : true);
  const [notificationTarget, setNotificationTarget] = useState('all');
  const [notificationRecipient, setNotificationRecipient] = useState('');
  const [notificationMessageText, setNotificationMessageText] = useState('');
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [morningNotificationMessage, setMorningNotificationMessage] = useState(null);

  const unreadCount = notifications.filter((notification) => !notification.read).length;

  const showToast = (message, type = 'info', duration = 3500) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), duration);
  };

  const handleViewMotivation = () => {
    const quote = getRandomMotivationQuote();
    showToast(quote, 'info', 6000);
  };

  const handleToggleNotifications = () => {
    setShowNotificationsPanel((prev) => !prev);
  };

  const handleSendNotification = async () => {
    const trimmedMessage = notificationMessageText.trim();
    if (!trimmedMessage) {
      return showToast('Please enter a notification message.', 'error');
    }

    const recipientIds = notificationTarget === 'all'
      ? allUsersData.map((userData) => userData.userId)
      : notificationRecipient ? [notificationRecipient] : [];

    if (recipientIds.length === 0) {
      return showToast('Please choose a valid recipient.', 'error');
    }

    try {
      await Promise.all(recipientIds.map((recipientId) => {
        const notifRef = push(ref(database, `notifications/${recipientId}`));
        return set(notifRef, {
          message: trimmedMessage,
          ts: Date.now(),
          from: user?.email || 'admin',
          read: false
        });
      }));

      showToast(`Notification sent to ${notificationTarget === 'all' ? 'all members' : 'the selected member'}.`, 'success');
      setNotificationMessageText('');
      if (notificationTarget === 'individual') {
        setNotificationRecipient('');
      }
    } catch (error) {
      console.error('Error sending notifications:', error);
      showToast('Unable to send notifications. Please try again.', 'error');
    }
  };

  const handleAdminEditNotification = async (notification) => {
    const newMessage = window.prompt('Edit notification message', notification.message);
    if (newMessage === null || newMessage.trim() === '') return;

    try {
      await set(ref(database, `notifications/${notification.userId}/${notification.id}`), {
        ...notification,
        message: newMessage.trim(),
        ts: notification.ts || Date.now()
      });
      showToast('Notification updated successfully.', 'success');
    } catch (error) {
      console.error('Error updating notification:', error);
      showToast('Unable to update notification. Please try again.', 'error');
    }
  };

  const handleAdminDeleteNotification = async (notification) => {
    if (!window.confirm('Delete this notification?')) return;
    try {
      await set(ref(database, `notifications/${notification.userId}/${notification.id}`), null);
      showToast('Notification deleted.', 'success');
    } catch (error) {
      console.error('Error deleting notification:', error);
      showToast('Unable to delete notification. Please try again.', 'error');
    }
  };

  useEffect(() => {
    const updateMessage = () => {
      if (!user || isAdminMode || hasCheckedInToday) {
        setMorningNotificationMessage(null);
        return;
      }

      const now = getISTTime();
      const message = getMorningNotificationMessage(now, hasCheckedInToday);
      setMorningNotificationMessage(message);
    };

    updateMessage();

    const intervalId = setInterval(updateMessage, 30000);
    return () => clearInterval(intervalId);
  }, [user, isAdminMode, hasCheckedInToday]);

  useEffect(() => {
    const updateSidebarVisibility = () => {
      const desktop = window.innerWidth > 1200;
      setIsDesktop(desktop);
      setShowSidebar(desktop ? true : false);
    };
    updateSidebarVisibility();
    window.addEventListener('resize', updateSidebarVisibility);
    return () => window.removeEventListener('resize', updateSidebarVisibility);
  }, []);

  const handleToggleSidebar = () => {
    if (isDesktop) {
      // keep sidebar visible on desktop
      setShowSidebar(true);
      return;
    }
    setShowSidebar((prev) => !prev);
  };

  const handleCloseSidebar = () => {
    if (isDesktop) return; // don't close on desktop
    setShowSidebar(false);
  };

  // Load user data
  const loadUserData = useCallback((userId) => {
    const logsRef = ref(database, 'checkInLogs/' + userId);
    const coinsRef = ref(database, 'userCoins/' + userId);
    const redemptionsRef = ref(database, 'userRedemptions/' + userId);
    const habitsRef = ref(database, 'userHabits/' + userId);
    const habitTrackingRef = ref(database, 'habitTracking/' + userId);

    onValue(logsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setLogs(data);

      // Check if user has checked in today by scanning entries
      const todayIso = getISTTime().toISOString().slice(0, 10);
      const entries = Object.values(data);
      const checkedIn = entries.some((entry) => {
        const ts = entry && (entry.ts || entry.timestamp);
        if (!ts) return false;
        const d = new Date(ts);
        return d.toISOString().slice(0, 10) === todayIso;
      });
      setHasCheckedInToday(checkedIn);
    });

    onValue(coinsRef, (snapshot) => {
      const data = snapshot.val() || 0;
      setTotalCoins(data);
    });

    onValue(redemptionsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setUserRedemptions(data);
    });

    onValue(habitsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setUserHabits(data);
    });

    onValue(habitTrackingRef, (snapshot) => {
      const data = snapshot.val() || {};
      setHabitTracking(data);
    });

    const notificationsRef = ref(database, `notifications/${userId}`);
    onValue(notificationsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const list = Object.entries(data).map(([id, item]) => ({ id, ...item }));
      list.sort((a, b) => (b.ts || 0) - (a.ts || 0));
      setNotifications(list);
    });
  }, []);

  // Load the public leaderboard from the same profile and coin records used by the dashboard.
  useEffect(() => {
    if (!user || isAdminMode) {
      setLeaderboardUsers([]);
      return;
    }

    let coinsData = {};
    let profilesData = {};

    const updateLeaderboard = () => {
      const users = Object.entries(profilesData)
        .map(([userId, profile]) => ({
          userId,
          name: typeof profile?.displayName === 'string' ? profile.displayName.trim() : '',
          coins: Number(coinsData[userId]) || 0
        }))
        .filter((userData) => userData.name)
        .sort((a, b) => b.coins - a.coins || a.name.localeCompare(b.name))
        .slice(0, 5);

      setLeaderboardUsers(users);
    };

    const unsubscribeCoins = onValue(ref(database, 'userCoins'), (snapshot) => {
      coinsData = snapshot.val() || {};
      updateLeaderboard();
    });
    const unsubscribeProfiles = onValue(ref(database, 'profiles'), (snapshot) => {
      profilesData = snapshot.val() || {};
      updateLeaderboard();
    });

    return () => {
      unsubscribeCoins();
      unsubscribeProfiles();
    };
  }, [user, isAdminMode]);

  // Load all users data for admin dashboard
  useEffect(() => {
    if (!isAdminMode) {
      console.log('Admin mode is false, skipping data load');
      return;
    }

    console.log('Admin mode effect triggered, isAdminMode =', isAdminMode);
    console.log('Current user:', user ? `${user.email} (UID: ${user.uid})` : 'No user logged in');

    // Check if user is authenticated and is the admin
    if (!user || user.email !== 'hellorblend@gmail.com') {
      console.log('Not authorized: user is not the admin');
      setAllUsersData([]);
      return;
    }

    console.log('User is authorized, fetching admin data...');
    console.log('Using user state for auth:', user.email);

    const allUsersRef = ref(database, 'userCoins');
    const checkInRef = ref(database, 'checkInLogs');
    const userProfilesRef = ref(database, 'profiles');
    const allNotificationsRef = ref(database, 'notifications');

    // Fetch all datasets
    Promise.all([
      get(allUsersRef),
      get(checkInRef),
      get(userProfilesRef),
      get(allNotificationsRef)
    ]).then(([coinsSnapshot, checkInSnapshot, profilesSnapshot, notificationsSnapshot]) => {
      console.log('✅ Data fetch successful');
      console.log('Coins snapshot received, exists:', coinsSnapshot.exists());
      console.log('Check-in snapshot received, exists:', checkInSnapshot.exists());
      console.log('Profiles snapshot received, exists:', profilesSnapshot.exists());
      
      const coinsData = coinsSnapshot.val() || {};
      const checkInData = checkInSnapshot.val() || {};
      const profilesData = profilesSnapshot.val() || {};
      
      console.log('Coins data loaded:', coinsData);
      console.log('Check-in data loaded:', checkInData);
      console.log('Profiles data loaded:', profilesData);
      console.log('Number of users with coins:', Object.keys(coinsData).length);
      console.log('Number of users with check-ins:', Object.keys(checkInData).length);
      console.log('Number of users with profiles:', Object.keys(profilesData).length);
      
      // Build users array from coins data (source of truth)
      const usersArray = [];
      
      for (const userId in coinsData) {
        const userLogs = checkInData[userId] || {};
        const totalCheckins = Object.keys(userLogs).length;
        const userProfile = profilesData[userId] || {};
        
        // Get last check-in date
        let lastCheckInDate = 'Never';
        const logsArray = Object.values(userLogs);
        if (logsArray.length > 0) {
          const mostRecent = logsArray.reduce((latest, entry) => {
            if (!entry || !entry.ts) return latest;
            const entryDate = new Date(entry.ts).getTime();
            const latestDate = (latest && latest.ts) ? new Date(latest.ts).getTime() : 0;
            return entryDate > latestDate ? entry : latest;
          }, null);
          
          if (mostRecent && mostRecent.ts) {
            const date = new Date(mostRecent.ts);
            lastCheckInDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          }
        }

        let lastLoginDate = 'Never';
        let lastLoginTs = null;
        const rawLastLogin = userProfile.lastLogin;
        if (rawLastLogin) {
          let resolvedTs = null;
          if (typeof rawLastLogin === 'object' && rawLastLogin !== null) {
            if (typeof rawLastLogin.seconds === 'number') {
              resolvedTs = rawLastLogin.seconds * 1000;
            } else if (typeof rawLastLogin.toString === 'function') {
              const maybeNum = Number(rawLastLogin.toString());
              if (!Number.isNaN(maybeNum)) resolvedTs = maybeNum;
            }
          } else {
            resolvedTs = Number(rawLastLogin);
          }
          if (!Number.isNaN(resolvedTs) && resolvedTs > 0) {
            lastLoginTs = resolvedTs;
            lastLoginDate = new Date(resolvedTs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          }
        }
        
        usersArray.push({
          userId,
          name: userProfile.displayName || '',
          email: userProfile.email || '',
          coins: coinsData[userId],
          totalCheckins,
          lastCheckIn: lastCheckInDate,
          lastLogin: lastLoginDate,
          lastLoginTs
        });
      }
      
      console.log('Updated users array:', usersArray);
      setAllUsersData(usersArray);

      const notificationsData = notificationsSnapshot.val() || {};
      const notificationList = [];
      Object.entries(notificationsData).forEach(([userId, userNotifications]) => {
        if (!userNotifications) return;
        Object.entries(userNotifications).forEach(([id, notification]) => {
          notificationList.push({
            id,
            userId,
            message: notification.message,
            ts: notification.ts,
            from: notification.from,
            read: notification.read
          });
        });
      });
      notificationList.sort((a, b) => (b.ts || 0) - (a.ts || 0));
      setAdminNotifications(notificationList);
    }).catch((error) => {
      console.error('❌ Error loading admin data:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        name: error.name
      });
      setAllUsersData([]);
    });

    const unsubscribeNotifications = onValue(allNotificationsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const notificationList = [];
      Object.entries(data).forEach(([userId, userNotifications]) => {
        if (!userNotifications) return;
        Object.entries(userNotifications).forEach(([id, notification]) => {
          notificationList.push({
            id,
            userId,
            message: notification.message,
            ts: notification.ts,
            from: notification.from,
            read: notification.read
          });
        });
      });
      notificationList.sort((a, b) => (b.ts || 0) - (a.ts || 0));
      setAdminNotifications(notificationList);
    });

    return () => {
      if (typeof unsubscribeNotifications === 'function') {
        unsubscribeNotifications();
      }
    };
  }, [isAdminMode, user]);

  // Check auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (user) {
        // Store user profile data (name and email) if not already stored
        const userProfileRef = ref(database, `profiles/${user.uid}`);
        get(userProfileRef).then((snapshot) => {
          if (!snapshot.exists()) {
            // Store user profile data
            set(userProfileRef, {
              displayName: user.displayName || '',
              email: user.email || '',
              createdAt: serverTimestamp(),
              lastLogin: serverTimestamp()
            }).catch(error => {
              console.error('Error storing user profile:', error);
            });
          } else {
            // Update last login time
            const existingData = snapshot.val();
            set(userProfileRef, {
              ...existingData,
              lastLogin: serverTimestamp()
            }).catch(error => {
              console.error('Error updating user profile:', error);
            });
          }
        }).catch(error => {
          console.error('Error checking user profile:', error);
        });

        loadUserData(user.uid);
        console.log('User logged in:', user.email, 'UID:', user.uid);
        // Disable admin mode if user is not admin
        if (user.email !== 'hellorblend@gmail.com') {
          setIsAdminMode(false);
        }
      } else {
        console.log('No user logged in');
        // Clear admin mode on logout
        setIsAdminMode(false);
      }
    });
    return () => unsubscribe();
  }, [loadUserData]);

  // Expose admin data to window for console debugging
  useEffect(() => {
    if (isAdminMode && allUsersData.length > 0) {
      window.adminData = allUsersData;
      console.log('✅ Admin data exposed to window.adminData - use it in console!');
    }
  }, [isAdminMode, allUsersData]);

  const filteredAdminData = allUsersData
    .filter((userData) => {
      if (adminMinCoins === '') return true;
      return Number(userData.coins) >= Number(adminMinCoins);
    })
    .filter((userData) => {
      if (!adminLastLoginAfter) return true;
      if (!userData.lastLoginTs) return false;
      const threshold = new Date(adminLastLoginAfter).setHours(0, 0, 0, 0);
      return userData.lastLoginTs >= threshold;
    });

  const sortedAdminData = [...filteredAdminData].sort((a, b) => {
    const getValue = (item) => {
      switch (adminSortField) {
        case 'coins':
          return Number(item.coins || 0);
        case 'totalCheckins':
          return Number(item.totalCheckins || 0);
        case 'lastLogin':
          return Number(item.lastLoginTs || 0);
        case 'name':
          return item.name ? item.name.toLowerCase() : '';
        default:
          return item[adminSortField] || 0;
      }
    };

    const aValue = getValue(a);
    const bValue = getValue(b);
    let comparison = 0;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      comparison = aValue.localeCompare(bValue);
    } else {
      comparison = bValue - aValue;
    }

    return adminSortDirection === 'asc' ? -comparison : comparison;
  });

  // Handle delete entry (delete a pushed check-in log by id)
  const handleDeleteEntry = (id) => {
    if (!user) return;
    if (window.confirm('Are you sure you want to delete this entry?')) {
      const entryRef = ref(database, `checkInLogs/${user.uid}/${id}`);
      // use set(..., null) to remove a node (update requires an object)
      set(entryRef, null)
        .then(() => {
          showToast('Entry deleted successfully!', 'success');
        })
        .catch(error => {
          console.error(error);
          showToast('Error deleting entry. Please try again.', 'error');
        });
    }
  };

  // ----- Deals & Feed data -----
  const deals = [
    { id: 'deal1', title: 'Morning Gift Box', cost: 200, desc: 'A curated gift pack for early risers.' },
    { id: 'deal2', title: '30 Day Happiness Mastery Program', cost: 250, desc: 'A guided 30-day program to build happier morning habits.' },
    { id: 'deal3', title: 'Get Morning Life Coach for a Month', cost: 400, desc: 'One month of morning coaching sessions to build consistent routines.' }
  ];

  const posts = [
    {
      id: 'p1',
      title: 'Small Habits That Improve Mornings',
      subtitle: 'Tiny daily changes that set the tone for your day',
      image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=80',
      summary: 'Simple, repeatable practices that make mornings smoother and more energizing.',
      body: `Small Habits That Improve Mornings

A calm, consistent morning starts with small choices repeated daily. You don't need a long, complicated routine — incremental habits build momentum and set the tone for the whole day.

1) Wake with Intention
Instead of hitting snooze, pick a realistic wake time and place your alarm away from the bed so you stand up to turn it off. Give yourself one clear intention for the morning (e.g. "take ten minutes to plan my day").

2) Hydrate Immediately
Drinking a glass of water within 10-15 minutes of waking replenishes fluids and helps digestion and alertness. Keep a water bottle by your bedside or on a nearby table.

3) Get Natural Light
Expose yourself to sunlight as soon as conveniently possible. Light helps reset your circadian rhythm, improves mood, and increases alertness. Open curtains or step outside for a 2–5 minute walk.

4) Move Briefly
A short bout of movement — 3–10 minutes of stretching, bodyweight exercises, or a quick walk — raises circulation and reduces morning grogginess.

5) Simplify Decision Friction
Prepare basics the night before: outfit, a quick breakfast, and a short to-do list. Reducing small decisions grants willpower for more meaningful tasks.

6) Plan 1–3 Priorities
Spend 3–5 minutes listing the top 1–3 things you want to complete before noon. Treat this as the day's focus, not a lengthy task dump.

7) Limit Phone Time
Delay social media or email for at least 20–30 minutes. Start with something that supports your intention: planning, reading, light journaling, or focused work.

8) Practice Gratitude or Reflection
Write one sentence of gratitude or one small win from yesterday. This shifts attention to what’s working and primes motivation.

Action Steps:
- Choose one habit from above and do it consistently for 7 days.
- Keep the habit under 10 minutes; small wins compound.

Small, consistent morning habits compound into greater energy, focus, and satisfaction. Start tiny, repeat daily, and expand gradually.`
    },
    {
      id: 'p2',
      title: 'Simple Mindfulness Routines',
      subtitle: 'Short practices to bring clarity and calm',
      image: 'https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=1200&q=80',
      summary: 'Short mindfulness practices to center your mind before the day begins.',
      body: `Simple Mindfulness Routines

Mindfulness in the morning doesn't require long sessions. Short, consistent practices help reduce stress and improve attention throughout the day.

1) Three-Breath Reset
Sit comfortably, close your eyes, and take three slow, full breaths. Inhale for 4 counts, hold 1–2 counts, exhale for 6–8 counts. This anchors attention and calms the nervous system.

2) Two-Minute Body Scan
Scan quickly from head to toe, noticing tension without judgment. Breathe into any tight spots for a few breaths, then continue.

3) Five-Minute Focused Attention
Set a timer for five minutes. Focus on the breath, counting each inhale and exhale up to ten, then restart. When attention wanders, gently return it — this trains concentration.

4) One-Sentence Journaling
Write one sentence about how you feel and one thing you intend to accomplish. This clarifies priorities and emotional state.

5) Mindful Movement
Pair simple stretching or yoga with slow, attentive breath. Notice sensations and transitions between poses.

Tips:
- Start with 2–5 minutes and increase if desired.
- Use a consistent cue (after brushing teeth, after dressing) to build habit.

Mindfulness needn't be elaborate — small moments of clarity in the morning lead to more thoughtful, less reactive days.`
    },
    {
      id: 'p3',
      title: 'How to Get More Done Before 9AM',
      subtitle: 'Protect your early hours for the highest-impact work',
      image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=80',
      summary: 'Practical strategies to accomplish high-value work in the early hours.',
      body: `How to Get More Done Before 9AM

Getting meaningful work done early is often about preparation and protecting your attention.

1) Prepare the Night Before
Choose one to three high-impact tasks for tomorrow and set out what you need. A short pre-sleep review reduces morning friction.

2) Protect Your First Hour
Treat the first 60 minutes as sacred work time: no social apps, no reactive inbox checks. Use it for your most important task.

3) Time-Block Simple Sprints
Use 25–50 minute focused sprints (Pomodoro-style). Before each sprint, write the exact outcome you want to achieve.

4) Reduce Friction
Place tools, files, or materials you need within easy reach. Remove distractions (put phone in another room or enable Do Not Disturb).

5) Use Caffeine Strategically
If you use caffeine, delay it 20–30 minutes until you’ve started your first focused work to avoid a mid-morning crash.

6) Start Small, Build Momentum
Begin with a small win (a 10–15 minute task) to create momentum, then continue into deeper work.

Sample Morning (6:30–9:00)
- 6:30 – 6:40: Wake, hydrate, and set one intention.
- 6:40 – 6:50: Brief movement/stretch.
- 6:50 – 7:00: Review top 1–3 tasks prepared last night.
- 7:00 – 8:00: Focus sprint on top priority.
- 8:00 – 8:15: Short break and review.
- 8:15 – 9:00: Second sprint or execution of quick tasks.

Consistency wins: by protecting early hours and focusing on one priority, you can accomplish more with less stress.`
    }
  ];

  // Check if current time is within deals window (4:00 - 7:30 IST)
  const isDealsWindow = () => {
    const now = getISTTime();
    const minutes = now.getHours() * 60 + now.getMinutes();
    return minutes >= (4 * 60) && minutes <= (7 * 60 + 30);
  };

  // Redeem a deal: ensure within time window and sufficient coins; atomically deduct and record redemption
  const redeemDeal = async (deal) => {
    if (!user) return showToast('Please sign in to redeem deals.', 'error');
    if (!isDealsWindow()) return showToast('Morning deals are available between 4:00 AM and 7:30 AM IST only.', 'error');
    
    const cost = deal.cost;
    const coinsRef = ref(database, `userCoins/${user.uid}`);
    
    try {
      // Check if user has sufficient coins for this specific deal
      const snapshot = await new Promise((resolve) => {
        onValue(coinsRef, resolve, { onlyOnce: true });
      });
      const currentCoins = snapshot.val() || 0;
      if (currentCoins < cost) {
        return showToast(`You need at least ${cost} morning coins to redeem this deal. You currently have ${currentCoins} coins.`, 'error');
      }
      
      const result = await runTransaction(coinsRef, (current) => {
        const curr = current || 0;
        if (curr < cost) return; // abort transaction
        return curr - cost;
      });
      if (!result.committed) {
        return showToast('Not enough coins to redeem this deal.', 'error');
      }
      
      // record redemption
      const redRef = push(ref(database, `userRedemptions/${user.uid}`));
      await set(redRef, { ts: serverTimestamp(), dealId: deal.id, cost: cost, title: deal.title });
      
      // Send WhatsApp message
      const message = `Hi, I would like to redeem the ${deal.title} offer. My email is ${user.email}`;
      const whatsappUrl = `https://api.whatsapp.com/send?phone=916364410218&text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      showToast(`Redeemed ${deal.title} for ${cost} coins!`, 'success');
    } catch (err) {
      console.error('Redeem error', err);
      showToast('Error redeeming deal. Please try again.', 'error');
    }
  };

  // Access a redeemed deal: open WhatsApp chat
  const accessDeal = (deal) => {
    if (!user) return showToast('Please sign in to access deals.', 'error');
    
    const message = `Hi, I would like to redeem the ${deal.title} offer. My email is ${user.email}`;
    const whatsappUrl = `https://api.whatsapp.com/send?phone=916364410218&text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Handle check in
  const handleCheckIn = async () => {
    if (!user) return;
    if (hasCheckedInToday) {
      showToast("You've already checked in today!", 'info');
      return;
    }

    const now = getISTTime();
    const reward = calculateReward(now);
    
    try {
      // Push a new log entry (serverTimestamp for ts)
      const logRef = push(ref(database, `checkInLogs/${user.uid}`));
      await set(logRef, {
        ts: serverTimestamp(),
        time: now.toLocaleTimeString(),
        reward: reward
      });

      // Atomically increment user coins to avoid race conditions
      const coinsRef = ref(database, `userCoins/${user.uid}`);
      await runTransaction(coinsRef, (current) => {
        return (current || 0) + reward;
      });

      if (reward > 0) {
        showToast(`Congratulations! You earned ${reward} morning coins!`, 'success');
      } else {
        showToast('Check-in recorded, but no coins earned. Try checking in earlier tomorrow!', 'info');
      }
    } catch (error) {
      console.error('Error recording check-in:', error);
      showToast('Error recording check-in. Please check your network or database rules and try again.', 'error');
    }
  };

  // Handle login
  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error signing in with Google:", error);
      switch (error.code) {
        case 'auth/popup-blocked':
          showToast("Please allow popups for this website to sign in with Google.", 'error');
          break;
        case 'auth/popup-closed-by-user':
          showToast("Sign-in was cancelled. Please try again.", 'info');
          break;
        case 'auth/network-request-failed':
          showToast("Network error. Please check your internet connection and try again.", 'error');
          break;
        default:
          showToast("Error signing in: " + error.message, 'error');
      }
    }
  };

  // Handle logout
  const handleLogout = () => {
    signOut(auth).catch((error) => {
      console.error("Error signing out:", error);
    });
  };

  // Login page
  if (!user) {
    return (
      <div className="login-container">
        <div className="login-card">
          <img
            className="login-illustration"
            src="https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=1600&q=80"
            alt="Scenic nature sunrise"
          />
          <div className="login-header">
            <h1>Welcome to MorningClub</h1>
            <p>Track your wake-ups and sleep patterns — feel better each morning.</p>
          </div>
          <button onClick={handleLogin} className="login-btn">
            <span className="google-icon">G</span> Continue with Google
          </button>
          <p className="footer-note">
            By continuing you agree to our{' '}
            <span 
              onClick={() => setShowTermsAndConditions(true)} 
              className="footer-link"
            >
              terms
            </span>
            {' '}and{' '}
            <span 
              onClick={() => setShowPrivacyPolicy(true)} 
              className="footer-link"
            >
              privacy policy
            </span>
            .
          </p>
        </div>

        {/* Privacy Policy Popup */}
        {showPrivacyPolicy && (
          <div className="modal-overlay" onClick={() => setShowPrivacyPolicy(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button 
                className="modal-close" 
                onClick={() => setShowPrivacyPolicy(false)}
              >
                ×
              </button>
              <h2>Privacy Policy</h2>
              <div className="modal-body">
                <h3>1. Information We Collect</h3>
                <p>We collect information you provide directly, including your name, email address, phone number, and usage data. We also collect data about your check-in times and earned coins through your account activity.</p>

                <h3>2. How We Use Your Information</h3>
                <p>We use your information to provide, maintain, and improve MorningClub services, process your check-ins, track rewards, and send notifications. Your data helps us understand user patterns and optimize the morning routine experience.</p>

                <h3>3. Data Security</h3>
                <p>We implement industry-standard security measures to protect your personal information. All data is encrypted and stored securely on Firebase servers with restricted access controls.</p>

                <h3>4. Third-Party Sharing</h3>
                <p>We do not sell or share your personal information with third parties, except as required by law or with your explicit consent. WhatsApp messages for deal redemption are sent directly through WhatsApp's official API.</p>

                <h3>5. Cookies and Tracking</h3>
                <p>We use cookies and similar technologies to remember your preferences and improve your experience. You can manage cookie preferences in your browser settings.</p>

                <h3>6. Your Rights</h3>
                <p>You have the right to access, update, or delete your personal information at any time. Contact us at support@morningclub.com to exercise these rights.</p>

                <h3>7. Policy Updates</h3>
                <p>We may update this privacy policy from time to time. We will notify you of significant changes via email or by posting the updated policy on our platform.</p>

                <h3>8. Contact Us</h3>
                <p>If you have questions about our privacy practices, please contact us at privacy@morningclub.com</p>
              </div>
              <button 
                className="modal-close-btn" 
                onClick={() => setShowPrivacyPolicy(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Terms and Conditions Popup */}
        {showTermsAndConditions && (
          <div className="modal-overlay" onClick={() => setShowTermsAndConditions(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button 
                className="modal-close" 
                onClick={() => setShowTermsAndConditions(false)}
              >
                ×
              </button>
              <h2>Terms and Conditions</h2>
              <div className="modal-body">
                <h3>1. Acceptance of Terms</h3>
                <p>By accessing and using MorningClub, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.</p>

                <h3>2. Use License</h3>
                <p>Permission is granted to temporarily download one copy of the materials (information or software) on MorningClub for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:</p>
                <ul>
                  <li>Modify or copy the materials</li>
                  <li>Use the materials for any commercial purpose or for any public display</li>
                  <li>Attempt to decompile or reverse engineer any software contained on the platform</li>
                  <li>Remove any copyright or other proprietary notations from the materials</li>
                </ul>

                <h3>3. Disclaimer</h3>
                <p>The materials on MorningClub are provided "as is". We make no warranties, expressed or implied, and hereby disclaim and negate all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.</p>

                <h3>4. Limitations</h3>
                <p>In no event shall MorningClub or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on MorningClub, even if we or an authorized representative has been notified orally or in writing of the possibility of such damage.</p>

                <h3>5. Accuracy of Materials</h3>
                <p>The materials appearing on MorningClub could include technical, typographical, or photographic errors. We do not warrant that any of the materials on MorningClub are accurate, complete, or current. We may make changes to the materials contained on MorningClub at any time without notice.</p>

                <h3>6. Links</h3>
                <p>We have not reviewed all of the sites linked to our website and are not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by us of the site. Use of any such linked website is at the user's own risk.</p>

                <h3>7. Modifications</h3>
                <p>We may revise these terms of service for MorningClub at any time without notice. By using this website, you are agreeing to be bound by the then current version of these terms of service.</p>

                <h3>8. Governing Law</h3>
                <p>These terms and conditions are governed by and construed in accordance with the laws of India, and you irrevocably submit to the exclusive jurisdiction of the courts in that location.</p>

                <h3>9. User Conduct</h3>
                <p>Users agree to use MorningClub responsibly and not engage in any unlawful or fraudulent activities. This includes but is not limited to hacking, unauthorized access, or misuse of rewards and coins.</p>

                <h3>10. Contact Information</h3>
                <p>For questions regarding these terms, please contact us at legal@morningclub.com</p>
              </div>
              <button 
                className="modal-close-btn" 
                onClick={() => setShowTermsAndConditions(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="dashboard-app">
      <aside className={`sidebar ${showSidebar ? 'sidebar-visible' : 'sidebar-hidden'}`}>
          <div className="brand"><div className="logo">✹</div>MorningClub</div>
          <button className="sidebar-close" onClick={handleCloseSidebar} aria-label="Close menu">×</button>
          <nav className="sidebar-nav">
            <button type="button" className={`nav-item ${activeTab === 'checkin' ? 'active' : ''}`} onClick={() => { setActiveTab('checkin'); setActiveArticle(null); }}>📅 Do Morning Check-in</button>
            <button type="button" className={`nav-item ${activeTab === 'habits' ? 'active' : ''}`} onClick={() => { setActiveTab('habits'); setActiveArticle(null); }}>📊 Build Morning Habits</button>
            <button type="button" className={`nav-item ${activeTab === 'deals' ? 'active' : ''}`} onClick={() => { setActiveTab('deals'); setActiveArticle(null); }}>🎧 Access Morning Deals</button>
            <button type="button" className={`nav-item ${activeTab === 'feed' ? 'active' : ''}`} onClick={() => { setActiveTab('feed'); setActiveArticle(null); }}>🔍 Explore Morning Feed</button>
          </nav>
          <div className="motivation-card">
            <h3>Small changes,<br />big mornings.</h3>
            <p>You've got this! 💜</p>
            <button type="button" className="motivation-btn" onClick={handleViewMotivation}>View Motivation</button>
            <div className="sun-card" />
          </div>
        </aside>
      {showSidebar && !isDesktop && <div className="sidebar-overlay" onClick={handleCloseSidebar} />}
      <div className="main-panel">
        <header className="topbar">
          <div className="topbar-left">
            <button
              type="button"
              className="menu-toggle"
              onClick={handleToggleSidebar}
              aria-label="Toggle sidebar"
            >
              ☰
            </button>
            <div className="page-title">
              {activeTab === 'checkin' ? 'Do Morning Check-in' :
               activeTab === 'habits' ? 'Build Morning Habits' :
               activeTab === 'deals' ? 'Access Morning Deals' :
               activeTab === 'feed' ? 'Explore Morning Feed' :
               activeTab === 'article' ? activeArticle?.title || 'Article' :
               'MorningClub'}
            </div>
            {morningNotificationMessage && (
              <div className="pill notification-pill">
                <i className="fas fa-bell"></i> {morningNotificationMessage}
              </div>
            )}
          </div>
          <div className="topbar-right">
            {!isAdminMode && (
              <div className="pill coins-pill">🪙 {totalCoins} coins</div>
            )}
              <button className="bell" onClick={handleToggleNotifications} title="Notifications">
                🔔
                {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
              </button>
              {showNotificationsPanel && (
                <div className="notifications-panel" onClick={(e) => e.stopPropagation()}>
                  <div className="notifications-panel-header">
                    <span>Notifications</span>
                    <button className="notifications-close" onClick={() => setShowNotificationsPanel(false)}>×</button>
                  </div>
                  <div className="notifications-list">
                    {notifications.length === 0 ? (
                      <div className="notification-empty">No new notifications</div>
                    ) : (
                      notifications.map((notification) => (
                        <div key={notification.id || notification.ts} className="notification-item">
                          <div className="notification-message">{notification.message}</div>
                          <div className="notification-meta">
                            <span>{notification.from || 'MorningClub'}</span>
                            <span>{notification.ts ? new Date(notification.ts).toLocaleString() : ''}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            <div className="user-mini">
              <div className="avatar">
                {(user && user.displayName) ? user.displayName.split(' ').map(s => s && s[0]).join('').slice(0, 2).toUpperCase() : 'U'}
              </div>
              <div>{user?.displayName || user?.email || 'Guest'}</div>
            </div>
            <button onClick={handleLogout} className="logout-btn small">Logout</button>
            {user && user.email === 'hellorblend@gmail.com' && (
              <button 
                onClick={() => setIsAdminMode(!isAdminMode)} 
                className={`admin-toggle-btn ${isAdminMode ? 'active' : ''}`}
                type="button"
              >
                <i className="fas fa-cog"></i>
                {isAdminMode ? 'Admin Mode' : 'User Mode'}
              </button>
            )}
          </div>
        </header>

        
        {isAdminMode ? (
        /* Admin Dashboard */
        <main className="dashboard-main admin-main">
          <div className="admin-section">
            <h2>Admin Dashboard - Users Overview</h2>
            <div className="admin-stats">
              <div className="stat-box">
                <div className="stat-number">{allUsersData.length}</div>
                <div className="stat-label">Total Users</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">{allUsersData.reduce((sum, u) => sum + u.coins, 0)}</div>
                <div className="stat-label">Total Coins Distributed</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">{allUsersData.reduce((sum, u) => sum + u.totalCheckins, 0)}</div>
                <div className="stat-label">Total Check-ins</div>
              </div>
            </div>

            <div className="admin-notification-section">
              <h3>Send Notifications</h3>
              <div className="admin-notification-form">
                <div className="admin-control-group">
                  <label htmlFor="notificationTarget">Notify</label>
                  <select
                    id="notificationTarget"
                    value={notificationTarget}
                    onChange={(e) => setNotificationTarget(e.target.value)}
                  >
                    <option value="all">All Members</option>
                    <option value="individual">Individual Member</option>
                  </select>
                </div>
                {notificationTarget === 'individual' && (
                  <div className="admin-control-group">
                    <label htmlFor="notificationRecipient">Member</label>
                    <select
                      id="notificationRecipient"
                      value={notificationRecipient}
                      onChange={(e) => setNotificationRecipient(e.target.value)}
                    >
                      <option value="">Select member</option>
                      {allUsersData.map((member) => (
                        <option key={member.userId} value={member.userId}>
                          {member.name || member.userId}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="admin-control-group admin-notification-message-group">
                  <label htmlFor="notificationMessageText">Message</label>
                  <textarea
                    id="notificationMessageText"
                    rows="3"
                    placeholder="Write your notification here..."
                    value={notificationMessageText}
                    onChange={(e) => setNotificationMessageText(e.target.value)}
                  />
                </div>
                <button className="send-notification-btn" onClick={handleSendNotification}>
                  Send Notification
                </button>
              </div>
              <div className="admin-notification-list">
                <h4>Sent Notifications</h4>
                {adminNotifications.length === 0 ? (
                  <div className="notification-empty">No sent notifications yet.</div>
                ) : (
                  <div className="notification-table">
                    {adminNotifications.map((notification) => {
                      const member = allUsersData.find((userData) => userData.userId === notification.userId);
                      return (
                        <div key={`${notification.userId}-${notification.id}`} className="notification-row">
                          <div className="notification-row-main">
                            <div className="notification-message">{notification.message}</div>
                            <div className="notification-meta">
                              <span>{member ? member.name || member.userId : notification.userId}</span>
                              <span>{notification.ts ? new Date(notification.ts).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, month: 'short', day: 'numeric' }) : ''}</span>
                            </div>
                          </div>
                          <div className="notification-actions">
                            <button className="notification-action-btn" onClick={() => handleAdminEditNotification(notification)}>Edit</button>
                            <button className="notification-action-btn delete" onClick={() => handleAdminDeleteNotification(notification)}>Delete</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="admin-controls">
              <div className="admin-control-group">
                <label htmlFor="adminSortField">Sort by</label>
                <select
                  id="adminSortField"
                  value={adminSortField}
                  onChange={(e) => setAdminSortField(e.target.value)}
                >
                  <option value="coins">Morning Coins</option>
                  <option value="totalCheckins">Total Check-ins</option>
                  <option value="lastLogin">Latest Login</option>
                  <option value="name">Name</option>
                </select>
              </div>
              <div className="admin-control-group">
                <label htmlFor="adminSortDirection">Order</label>
                <select
                  id="adminSortDirection"
                  value={adminSortDirection}
                  onChange={(e) => setAdminSortDirection(e.target.value)}
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
              <div className="admin-control-group">
                <label htmlFor="adminMinCoins">Min coins</label>
                <input
                  id="adminMinCoins"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={adminMinCoins}
                  onChange={(e) => setAdminMinCoins(e.target.value)}
                />
              </div>
              <div className="admin-control-group">
                <label htmlFor="adminLastLoginAfter">Last login after</label>
                <input
                  id="adminLastLoginAfter"
                  type="date"
                  value={adminLastLoginAfter}
                  onChange={(e) => setAdminLastLoginAfter(e.target.value)}
                />
              </div>
            </div>

            <div className="users-table-container">
              <h3>Users List</h3>
              {allUsersData.length > 0 ? (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>User ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Morning Coins</th>
                      <th>Total Check-ins</th>
                      <th>Latest Login</th>
                      <th>Last Check-in</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAdminData.map((userData) => (
                      <tr key={userData.userId}>
                        <td className="user-id">{userData.userId}</td>
                        <td className="name-col">{userData.name || '-'}</td>
                        <td className="email-col">{userData.email || '-'}</td>
                        <td className="coins-col">{userData.coins}</td>
                        <td className="checkins-col">{userData.totalCheckins}</td>
                        <td className="checkins-col">{userData.lastLogin}</td>
                        <td className="lastcheckin-col">{userData.lastCheckIn}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-data">
                  <p>Loading user data...</p>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                    If this persists, check that you're logged in as the admin user (hellorblend@gmail.com)
                    and that there are users with check-in data in the database.
                  </p>
                </div>
              )}
            </div>
          </div>
        </main>
      ) : (
        /* Normal User Dashboard */
        <main className="dashboard-main">
        <div className="tracker-section">
          <div className="tracker-card">
            <h2>{
              activeTab === 'checkin' ? 'Daily Check-in' :
              activeTab === 'habits' ? 'Morning Habits' :
              activeTab === 'deals' ? 'Morning Deals' :
              activeTab === 'feed' ? 'Morning Feed' :
              (activeArticle ? activeArticle.title : 'Article')
            }</h2>
                <div className="tab-content">
                  {activeTab === 'checkin' && (
                    <>
                      <div className="rewards-info">
                        <h3>Today's Rewards:</h3>
                        <ul className="rewards-list">
                          <li><span className="time">4:00 AM - 4:59 AM:</span> <span className="coins">25 coins</span></li>
                          <li><span className="time">5:00 AM - 5:59 AM:</span> <span className="coins">10 coins</span></li>
                          <li><span className="time">6:00 AM - 6:59 AM:</span> <span className="coins">5 coins</span></li>
                          <li><span className="time">7:00 AM - 7:20 AM:</span> <span className="coins">2 coins</span></li>
                        </ul>
                      </div>
                      <div className="check-in-section">
                        <button 
                          onClick={handleCheckIn} 
                          className={`check-in-btn ${hasCheckedInToday ? 'checked-in' : ''}`}
                          disabled={hasCheckedInToday}
                        >
                          {hasCheckedInToday ? 'Already Checked In Today!' : 'Check In Now'}
                        </button>
                      </div>

                      <div className="checkin-metrics-grid">
                        {(() => {
                          const metrics = computeDailyMetrics(logs);
                          return (
                            <>
                              <div className="checkin-metric-card">
                                <div className="metric-label">Average Check-in</div>
                                <div className="metric-value">{metrics.averageLabel}</div>
                                <div className="metric-subtext">Based on {metrics.daysCheckedIn} day{metrics.daysCheckedIn === 1 ? '' : 's'} of data</div>
                              </div>
                              <div className="checkin-metric-card">
                                <div className="metric-label">Current Streak</div>
                                <div className="metric-value">{metrics.streak} day{metrics.streak === 1 ? '' : 's'}</div>
                                <div className="metric-subtext">Consecutive IST check-ins</div>
                              </div>
                              <div className="checkin-metric-card">
                                <div className="metric-label">Consistency Score</div>
                                <div className="metric-value">{metrics.consistencyScore}</div>
                                <div className="metric-subtext">Score from streak + weekly consistency</div>
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      <div className="leaderboard-section">
                        <div className="leaderboard-heading">
                          <h3>Morning Coins Leaderboard</h3>
                          <span>Top 5</span>
                        </div>
                        {leaderboardUsers.length > 0 ? (
                          <ol className="leaderboard-list">
                            {leaderboardUsers.map((leaderboardUser, index) => (
                              <li key={leaderboardUser.userId} className="leaderboard-row">
                                <span className="leaderboard-rank">{index + 1}</span>
                                <span className="leaderboard-name">{leaderboardUser.name}</span>
                                <span className="leaderboard-coins">{leaderboardUser.coins} coins</span>
                              </li>
                            ))}
                          </ol>
                        ) : (
                          <p className="leaderboard-empty">No named users yet.</p>
                        )}
                      </div>

                      <div className="entries-list">
                        <h3>Past Check-ins</h3>
                        
                        {/* Check-in Graph */}
                        {Object.keys(logs).length > 0 && (
                          <div className="chart-container">
                            <h4>Check-in Times (Last 7 Days) - Earlier is Better ↑</h4>
                            <ResponsiveContainer width="100%" height={300}>
                              <LineChart data={(() => {
                                const last7Days = {};
                                const now = getISTTime();
                                for (let i = 6; i >= 0; i--) {
                                  const date = new Date(now);
                                  date.setDate(date.getDate() - i);
                                  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                  last7Days[dateStr] = null;
                                }
                                Object.values(logs).forEach(entry => {
                                  if (entry && entry.ts) {
                                    const entryDate = new Date(entry.ts);
                                    const utcTime = entryDate.getTime() + (entryDate.getTimezoneOffset() * 60000);
                                    const istDate = new Date(utcTime + (5.5 * 60 * 60 * 1000));
                                    const dateStr = istDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                    if (last7Days.hasOwnProperty(dateStr)) {
                                      // Extract hour and minute from check-in time (e.g., "5:30:45 AM" -> 5.5)
                                      if (entry.time) {
                                        const timeParts = entry.time.match(/(\d+):(\d+)/);
                                        if (timeParts) {
                                          const hours = parseInt(timeParts[1], 10);
                                          const minutes = parseInt(timeParts[2], 10);
                                          const timeInHours = hours + (minutes / 60);
                                          // Invert score: 4 AM = 20, 10 AM = -6 (clamped to 0)
                                          // Formula: max(0, 24 - timeInHours)
                                          const invertedScore = Math.max(0, 24 - timeInHours);
                                          last7Days[dateStr] = invertedScore;
                                        }
                                      }
                                    }
                                  }
                                });
                                return Object.entries(last7Days).map(([date, time]) => ({ 
                                  date, 
                                  time: time !== null ? parseFloat(time.toFixed(2)) : null 
                                })).filter(d => d.time !== null);
                              })()}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis label={{ value: 'Early Wake Score (24 = 4 AM, 0 = After 10 AM)', angle: -90, position: 'insideLeft' }} domain={[0, 24]} />
                                <Tooltip formatter={(value) => {
                                  const originalHour = 24 - value;
                                  return `Score: ${value.toFixed(1)} (${Math.floor(originalHour)}:${String(Math.round((originalHour % 1) * 60)).padStart(2, '0')})`;
                                }} />
                                <Legend />
                                <Line type="monotone" dataKey="time" stroke="#2ecc71" strokeWidth={2} dot={{ fill: '#2ecc71', r: 5 }} name="Early Wake Score" />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                        
                        <div className="entries-container">
                          {Object.entries(logs)
                            .sort((a, b) => {
                              const ea = a[1] && (a[1].ts || a[1].timestamp) ? (a[1].ts || a[1].timestamp) : 0;
                              const eb = b[1] && (b[1].ts || b[1].timestamp) ? (b[1].ts || b[1].timestamp) : 0;
                              return eb - ea;
                            })
                            .map(([id, entry]) => {
                              const ts = entry && (entry.ts || entry.timestamp) ? (entry.ts || entry.timestamp) : null;
                              const displayDate = ts ? new Date(ts).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                              }) : '-';
                              const displayTime = entry && (entry.time || entry.timeString) ? (entry.time || entry.timeString) : '-';
                              return (
                                <div key={id} className="entry-card">
                                  <div className="entry-date">{displayDate}</div>
                                  <div className="entry-details">
                                    <div className="check-in-time">
                                      <i className="fas fa-clock"></i> {displayTime}
                                    </div>
                                    <div className="earned-coins">
                                      <i className="fas fa-coins"></i> {entry.reward} earned
                                    </div>
                                  </div>
                                  <div className="entry-actions">
                                    <button 
                                      onClick={() => handleDeleteEntry(id)}
                                      className="delete-btn"
                                      title="Delete entry"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </>
                  )}

                  {activeTab === 'habits' && (
                    <MorningHabits
                      user={user}
                      database={database}
                      userHabits={userHabits}
                      habitTracking={habitTracking}
                      showToast={showToast}
                      getISTTime={getISTTime}
                    />
                  )}

                  {activeTab === 'deals' && (
                    <div className="deals-list">
                      {deals.map((d) => {
                        const isRedeemed = Object.values(userRedemptions).some(red => red && red.dealId === d.id);
                        return (
                          <div key={d.id} className="deal-card">
                            <div className="deal-info">
                              <h4>{d.title}</h4>
                              <p className="deal-desc">{d.desc}</p>
                            </div>
                            <div className="deal-actions">
                              <div className="deal-cost">{d.cost} coins</div>
                              {isRedeemed ? (
                                <button className="access-btn" onClick={() => accessDeal(d)}>Access</button>
                              ) : (
                                <button className="redeem-btn" onClick={() => redeemDeal(d)}>Redeem</button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Toast */}
                  {toast && (
                    <div className={`toast ${toast.type}`}>
                      <div className="toast-message">{toast.message}</div>
                      <button className="toast-close" onClick={() => setToast(null)}>×</button>
                    </div>
                  )}

                  {activeTab === 'feed' && (
                    <div className="feed-list">
                        {posts.map(p => (
                        <div key={p.id} className="post-card" onClick={() => { setActiveArticle(p); setActiveTab('article'); }}>
                          {p.image && <img className="post-image" src={p.image} alt={p.title} />}
                          <div className="post-content">
                            <h4 className="post-title">{p.title}</h4>
                            {p.subtitle && <div className="post-subtitle">{p.subtitle}</div>}
                            <p className="post-summary">{p.summary}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {activeTab === 'article' && activeArticle && (
                    <div className="article-card">
                      <button className="cancel-btn" onClick={() => { setActiveArticle(null); setActiveTab('feed'); }} style={{marginBottom:12}}>← Back to Feed</button>
                      {activeArticle.image && <img className="article-hero" src={activeArticle.image} alt={activeArticle.title} />}
                      <h2>{activeArticle.title}</h2>
                      {activeArticle.subtitle && <h3 className="article-subtitle">{activeArticle.subtitle}</h3>}
                      <div className="article-body">
                        {String(activeArticle.body).split('\n\n').map((para, idx) => {
                          const lines = para.split('\n');
                          return (
                            <p key={idx}>
                              {lines.map((line, i) => (
                                <React.Fragment key={i}>
                                  {line}
                                  {i < lines.length - 1 && <br />}
                                </React.Fragment>
                              ))}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
          </div>
        </div>

        <div className="profile-section">
          <div className="profile-card">
            <div className="profile-header">
              <div className="profile-avatar">
                {user.photoURL ? (
                  <img src={user.photoURL} alt="Profile" className="profile-image" />
                ) : (
                  <div className="profile-initials">
                    {(user.displayName || user.email || '')
                      .split(' ')
                      .map(s => s && s[0])
                      .filter(Boolean)
                      .join('')
                      .slice(0,2)
                      .toUpperCase()}
                  </div>
                )}
              </div>
              <div className="profile-info">
                <h3>{user.displayName || 'Welcome!'}</h3>
                <p>{user.email}</p>
                <div className="profile-stats">
                  <div className="stat-item">
                    <span className="stat-value">{Object.keys(logs).length}</span>
                    <span className="stat-label">Total Check-ins</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{totalCoins}</span>
                    <span className="stat-label">Total Morning Coins</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{totalCoins}</span>
                    <span className="stat-label">Total Earnings</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">
                      {new Date(user.metadata.creationTime).toLocaleDateString('en-US', {
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                    <span className="stat-label">Member Since</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </main>
      )}
      </div>

      {/* Morning Chatbot */}
      {user && !isAdminMode && (
        <MorningChatbot
          user={user}
          onNavigate={(tab) => {
            setActiveTab(tab);
            setActiveArticle(null);
          }}
        />
      )}

      {/* article page is rendered inline inside the tabs (no modal). */}
    </div>
  );
}

export default App;